import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowLeft, Users, Building2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { basecampApi } from '@/lib/api';

interface Props {
  accountId: string;
  selectedProjectIds: number[];
  defaultOrgId: string;
  defaultOrgName: string;
  projectOrgMap: Map<number, string>; // basecampProjectId -> orgName
  onConfirm: (assignments: Map<string, string>) => void;
  onBack: () => void;
}

export function BasecampMemberPreview({
  accountId,
  selectedProjectIds,
  defaultOrgId,
  defaultOrgName,
  projectOrgMap,
  onConfirm,
  onBack,
}: Props) {
  const [manualAssignments, setManualAssignments] = useState<Map<string, string>>(new Map());

  // Get unique pending org names from the project map
  const pendingOrgNames = useMemo(() => [...new Set(projectOrgMap.values())], [projectOrgMap]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['basecamp-member-preview', accountId, selectedProjectIds],
    queryFn: () => basecampApi.previewMembers({
      accountId,
      projectIds: selectedProjectIds,
      pendingOrgNames,
    }),
  });

  // Helper to get the default org for a user based on their project membership
  const getDefaultOrgForUser = (userProjects: number[]): { id: string; name: string } => {
    // Find the first project this user is on that has an org name
    for (const projectId of userProjects) {
      const orgName = projectOrgMap.get(projectId);
      if (orgName) {
        return { id: `pending:${orgName}`, name: orgName };
      }
    }
    // Fallback to the agency org
    return { id: defaultOrgId, name: defaultOrgName };
  };

  const { matched, unmatched, allOrgs } = useMemo(() => {
    if (!data) return { matched: [], unmatched: [], allOrgs: [] };

    const m = data.assignments.filter(a => a.orgId !== null);
    const u = data.assignments.filter(a => a.orgId === null);

    // Build org list: existing matched orgs + pending orgs
    const orgList: Array<{ id: string; name: string; pending?: boolean }> = [];
    const seen = new Set<string>();

    // Add existing matched orgs
    for (const org of data.organizations) {
      if (!seen.has(org.id)) {
        seen.add(org.id);
        orgList.push({ id: org.id, name: org.name });
      }
    }

    // Add pending orgs (will be created during import)
    if (data.pendingOrganizations) {
      for (const org of data.pendingOrganizations) {
        const pendingId = `pending:${org.name}`;
        if (!seen.has(pendingId)) {
          seen.add(pendingId);
          orgList.push({ id: pendingId, name: org.name, pending: true });
        }
      }
    }

    // Add the agency org as fallback option
    if (!seen.has(defaultOrgId)) {
      orgList.push({ id: defaultOrgId, name: defaultOrgName });
    }

    return {
      matched: m,
      unmatched: u,
      allOrgs: orgList,
    };
  }, [data, defaultOrgId, defaultOrgName]);

  const handleAssign = (email: string, orgId: string) => {
    setManualAssignments(prev => {
      const next = new Map(prev);
      next.set(email, orgId);
      return next;
    });
  };

  const handleConfirm = () => {
    const assignments = new Map<string, string>();

    // Add auto-matched
    for (const a of matched) {
      if (a.orgId) {
        assignments.set(a.email, a.orgId);
      }
    }

    // Add assignments for unmatched users
    for (const member of unmatched) {
      // Use manual assignment if set, otherwise use the project-based default
      const manualOrg = manualAssignments.get(member.email);
      if (manualOrg) {
        assignments.set(member.email, manualOrg);
      } else {
        const defaultOrg = getDefaultOrgForUser(member.projects);
        assignments.set(member.email, defaultOrg.id);
      }
    }

    onConfirm(assignments);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-grey-400 animate-spin mr-3" />
        <span className="text-sm text-grey-500">Loading member assignments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-grey-600">
          Failed to preview members: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-grey-500" />
        <h3 className="text-sm font-semibold text-secondary">Member Organization Assignment</h3>
      </div>
      <p className="text-sm text-grey-500">
        Review which organization each member will be assigned to. Members already assigned to an organization are not shown.
      </p>

      {/* Matched members */}
      {matched.length > 0 && (
        <div>
          <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-2">
            Matched by Domain ({matched.length})
          </p>
          <div className="border border-grey-200 rounded-lg divide-y divide-grey-100 max-h-48 overflow-y-auto">
            {matched.map(member => (
              <div key={member.email} className="flex items-center justify-between px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm text-secondary truncate">{member.name}</p>
                  <p className="text-xs text-grey-400 truncate">{member.email}</p>
                </div>
                <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                  {member.orgName}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unmatched members */}
      {unmatched.length > 0 && (
        <div>
          <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-2">
            Needs Assignment ({unmatched.length})
          </p>
          <div className="border border-amber-200 bg-amber-50/30 rounded-lg divide-y divide-amber-100">
            {unmatched.map(member => {
              const defaultOrg = getDefaultOrgForUser(member.projects);
              const currentValue = manualAssignments.get(member.email) || defaultOrg.id;

              return (
                <div key={member.email} className="flex items-center justify-between px-3 py-2 gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-secondary truncate">{member.name}</p>
                    <p className="text-xs text-grey-400 truncate">{member.email}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Building2 className="w-3.5 h-3.5 text-grey-400" />
                    <select
                      value={currentValue}
                      onChange={(e) => handleAssign(member.email, e.target.value)}
                      className="text-xs border border-grey-200 rounded px-2 py-1 bg-white min-w-[140px]"
                      aria-label={`Assign organization for ${member.name}`}
                    >
                      {allOrgs.map(org => (
                        <option key={org.id} value={org.id}>
                          {org.name}{org.pending ? ' (new)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-grey-400 mt-1.5">
            Each member defaults to their project's organization.
          </p>
        </div>
      )}

      {/* No members */}
      {matched.length === 0 && unmatched.length === 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-grey-500">No new members to assign. All members are already in organizations.</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-grey-100">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </Button>
        <Button onClick={handleConfirm}>
          Confirm & Import
        </Button>
      </div>
    </div>
  );
}
